import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './../styles/staff/Staff.css';
import './../styles/general/Reset.css';
import './../styles/units/ManageLocals.css';
import { useAuth } from './../general/AuthContext';

function UnassignedUnits() {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  const [unassignedUnitList, setUnassignedUnitList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [, setStaffReps] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [serviceReps, setServiceReps] = useState([]);
  const [fieldReps, setFieldReps] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'LOCAL',
    direction: 'ascending',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedUnit, setExpandedUnit] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle employee selection change
  const handleEmployeeChange = (unitId, employeeNumber, event) => {
    setSelectedEmployees(prevState => ({
      ...prevState,
      [unitId]: {
        ...prevState[unitId],
        [`employee${employeeNumber}`]: event.target.value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = unitId => {
    const selectedEmployeesForUnit = selectedEmployees[unitId] || {};
    
    const employee1Id = String(selectedEmployeesForUnit.employee1 || '');
    const employee2Id = String(selectedEmployeesForUnit.employee2 || '');
    const employee3Id = String(selectedEmployeesForUnit.employee3 || '');
    const employee4Id = String(selectedEmployeesForUnit.employee4 || '');
    const employee5Id = String(selectedEmployeesForUnit.employee5 || '');
    const employee6Id = String(selectedEmployeesForUnit.employee6 || '');
    
    const employee1Name = employees.find(emp => String(emp.EmployeeId) === employee1Id)?.full_name || '';
    const employee2Name = employees.find(emp => String(emp.EmployeeId) === employee2Id)?.full_name || '';
    const employee3Name = serviceReps.find(emp => String(emp.EmployeeId) === employee3Id)?.full_name || '';
    const employee4Name = fieldReps.find(emp => String(emp.EmployeeId) === employee4Id)?.full_name || '';
    const employee5Name = organizers.find(emp => String(emp.EmployeeId) === employee5Id)?.full_name || '';
    const employee6Name = fieldReps.find(emp => String(emp.EmployeeId) === employee6Id)?.full_name || '';
    
    const submission = {
      ...selectedEmployeesForUnit,
      employee1Name,
      employee2Name,
      employee3Name,
      employee4Name,
      employee5Name,
      employee6Name,
    };

    fetch(`/api/assign-employees/${unitId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify(submission),
    })
      .then(response => {
        if (response.ok) {
          // Remove the assigned unit from the list
          setUnassignedUnitList(prevList => 
            prevList.filter(unit => unit.id !== unitId)
          );
          
          // Show success message
          alert('Employees assigned successfully');
        } else {
          alert('Error assigning employees: ' + response.statusText);
        }
      })
      .catch(error => {
        console.error('Error assigning employees: ', error);
        alert('Error assigning employees');
      });
  };

  // Fetch all required data when component mounts
  useEffect(() => {
    if (!auth.isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsLoading(true);

    // Fetch all data in parallel
    Promise.all([
      fetch('/api/staff-reps', {
        headers: { Authorization: `Bearer ${auth.token}` },
      }).then(res => res.json()),
      
      fetch('/api/service-reps', {
        headers: { Authorization: `Bearer ${auth.token}` },
      }).then(res => res.json()),
      
      fetch('/api/organizers', {
        headers: { Authorization: `Bearer ${auth.token}` },
      }).then(res => res.json()),
      
      fetch('/api/field-reps', {
        headers: { Authorization: `Bearer ${auth.token}` },
      }).then(res => res.json()),
      
      fetch('/api/unassigned-units', {
        headers: { Authorization: `Bearer ${auth.token}` },
      }).then(res => res.json()),
    ])
      .then(([staffData, serviceData, organizerData, fieldData, unitsData]) => {
        setEmployees(staffData);
        setStaffReps(staffData);
        setServiceReps(serviceData);
        setOrganizers(organizerData);
        setFieldReps(fieldData);
        setUnassignedUnitList(unitsData);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        setAuth({ isAuthenticated: false, token: null });
        navigate('/login');
      });
  }, [auth, navigate, setAuth]);

  // Sort data when column header is clicked
  const sortData = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    setUnassignedUnitList(unassignedUnitList =>
      [...unassignedUnitList].sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      })
    );
  };

  return (
    <div className="page-container">
      <div className="content-wrapper">
        <header className="page-header">
          <h1 className="page-title">Unassigned Bargaining Units</h1>
        </header>
        
        <main className="main-content">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : unassignedUnitList.length === 0 ? (
            <div className="empty-state">
              <div className="success-icon">✓</div>
              <h2>All Units Assigned</h2>
              <p>There are currently no unassigned bargaining units.</p>
            </div>
          ) : isMobile ? (
            // Mobile View - Card Layout
            <div className="unassigned-mobile-container">
              <div className="mobile-sort-controls">
                <select 
                  className="mobile-sort-select"
                  value={sortConfig.key}
                  onChange={(e) => sortData(e.target.value)}
                >
                  <option value="LOCAL">Sort by Local</option>
                  <option value="UNIT">Sort by Unit</option>
                  <option value="NAME">Sort by Name</option>
                  <option value="TOWN">Sort by Town</option>
                </select>
              </div>
              
              <div className="unassigned-cards-container">
                {unassignedUnitList.map(unit => (
                  <div key={unit.id} className="unassigned-card">
                    <div className="unassigned-card-header">
                      <div className="unit-info-header">
                        <span className="unit-local-badge">Local {unit.LOCAL}</span>
                        <span className="unit-number">Unit {unit.UNIT.toString().padStart(4, '0')}</span>
                      </div>
                      <button 
                        className="expand-button"
                        onClick={() => setExpandedUnit(expandedUnit === unit.id ? null : unit.id)}
                        aria-label={expandedUnit === unit.id ? "Collapse" : "Expand"}
                      >
                        {expandedUnit === unit.id ? '−' : '+'}
                      </button>
                    </div>
                    
                    <Link to={`/unit-info/${unit.id}`} className="unit-name-link">
                      <h3 className="unit-name">{unit.NAME}</h3>
                    </Link>
                    
                    <div className="unit-town">
                      <span className="label">Town:</span>
                      <span className="value">{unit.TOWN}</span>
                    </div>
                    
                    {expandedUnit === unit.id && (
                      <div className="assignment-section">
                        <h4>Assign Representatives</h4>
                        
                        <div className="assignment-field">
                          <label>Staff Rep</label>
                          <select 
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee1 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 1, event)}
                          >
                            <option value="">Select Staff Rep</option>
                            {employees.map(employee => (
                              <option key={employee.EmployeeId} value={employee.EmployeeId}>
                                {employee.full_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="assignment-field">
                          <label>Service Rep</label>
                          <select 
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee3 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 3, event)}
                          >
                            <option value="">Select Service Rep</option>
                            {serviceReps.map(serviceRep => (
                              <option key={serviceRep.EmployeeId} value={serviceRep.EmployeeId}>
                                {serviceRep.full_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="assignment-field">
                          <label>Field Rep</label>
                          <select 
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee4 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 4, event)}
                          >
                            <option value="">Select Field Rep</option>
                            {fieldReps.map(fieldRep => (
                              <option key={fieldRep.EmployeeId} value={fieldRep.EmployeeId}>
                                {fieldRep.full_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="assignment-field">
                          <label>Field Rep 2</label>
                          <select
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee6 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 6, event)}
                          >
                            <option value="">Select Field Rep 2</option>
                            {fieldReps.map(fieldRep => (
                              <option key={fieldRep.EmployeeId} value={fieldRep.EmployeeId}>
                                {fieldRep.full_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="assignment-field">
                          <label>Organizer</label>
                          <select 
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee5 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 5, event)}
                          >
                            <option value="">Select Organizer</option>
                            {organizers.map(organizer => (
                              <option key={organizer.EmployeeId} value={organizer.EmployeeId}>
                                {organizer.full_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="assignment-field">
                          <label>Staff Rep 2</label>
                          <select 
                            className="mobile-form-select"
                            value={selectedEmployees[unit.id]?.employee2 || ''}
                            onChange={event => handleEmployeeChange(unit.id, 2, event)}
                          >
                            <option value="">Select Staff Rep 2</option>
                            {employees.map(employee => (
                              <option key={employee.EmployeeId} value={employee.EmployeeId}>
                                {employee.full_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <button 
                          className="mobile-assign-button"
                          onClick={() => handleSubmit(unit.id)}
                        >
                          Assign Representatives
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Desktop View - Table Layout
            <div className="table-container">
              <table className="data-table unassigned-units-table">
                <thead>
                  <tr>
                    {['LOCAL', 'UNIT', 'NAME', 'TOWN'].map(column => (
                      <th
                        key={column}
                        onClick={() => sortData(column)}
                        className={`sortable-header ${sortConfig.key === column ? `sorted-${sortConfig.direction}` : ""}`}
                      >
                        {column}
                        {sortConfig.key === column && (
                          <span className="sort-indicator">
                            {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                          </span>
                        )}
                      </th>
                    ))}
                    <th>Staff Rep</th>
                    <th>Service Rep</th>
                    <th>Field Rep</th>
                    <th>Organizer</th>
                    <th>Staff Rep 2</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedUnitList.map(unit => (
                    <tr key={unit.id} className="data-row">
                      <td className="cell">{unit.LOCAL}</td>
                      <td className="cell">{unit.UNIT.toString().padStart(4, '0')}</td>
                      <td className="cell">
                        <Link to={`/unit-info/${unit.id}`} className="table-link">{unit.NAME}</Link>
                      </td>
                      <td className="cell">{unit.TOWN}</td>
                      <td className="cell">
                        <select 
                          className="form-select"
                          value={selectedEmployees[unit.id]?.employee1 || ''}
                          onChange={event => handleEmployeeChange(unit.id, 1, event)}
                        >
                          <option value="">--</option>
                          {employees.map(employee => (
                            <option key={employee.EmployeeId} value={employee.EmployeeId}>
                              {employee.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell">
                        <select 
                          className="form-select"
                          value={selectedEmployees[unit.id]?.employee3 || ''}
                          onChange={event => handleEmployeeChange(unit.id, 3, event)}
                        >
                          <option value="">--</option>
                          {serviceReps.map(serviceRep => (
                            <option key={serviceRep.EmployeeId} value={serviceRep.EmployeeId}>
                              {serviceRep.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell">
                        <select 
                          className="form-select"
                          value={selectedEmployees[unit.id]?.employee4 || ''}
                          onChange={event => handleEmployeeChange(unit.id, 4, event)}
                        >
                          <option value="">--</option>
                          {fieldReps.map(fieldRep => (
                            <option key={fieldRep.EmployeeId} value={fieldRep.EmployeeId}>
                              {fieldRep.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell">
                        <select 
                          className="form-select"
                          value={selectedEmployees[unit.id]?.employee5 || ''}
                          onChange={event => handleEmployeeChange(unit.id, 5, event)}
                        >
                          <option value="">--</option>
                          {organizers.map(organizer => (
                            <option key={organizer.EmployeeId} value={organizer.EmployeeId}>
                              {organizer.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell">
                        <select 
                          className="form-select"
                          value={selectedEmployees[unit.id]?.employee2 || ''}
                          onChange={event => handleEmployeeChange(unit.id, 2, event)}
                        >
                          <option value="">--</option>
                          {employees.map(employee => (
                            <option key={employee.EmployeeId} value={employee.EmployeeId}>
                              {employee.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell">
                        <button 
                          className="action-button"
                          onClick={() => handleSubmit(unit.id)}
                        >
                          Assign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default UnassignedUnits;